'use client';
import Link from 'next/link';

import { Checkbox } from '#/components/ui/checkbox';
import Tooltip from '#/components/base-tooltip';
import { DataTable, DataTableProps } from '#/components/ui/data-table';
import { DataTable as RemoteDataTable } from '#/components/ui/remote-data-table';
import { Button } from '#/components/ui/button';
import { Skeleton } from '#/components/ui/skeleton';
import { ReadonlyURLSearchParams } from 'next/navigation';
import {
  COLUMN_VISIBLE_HIDE_KEY,
  COLUMN_VISIBLE_SHOW_KEY
} from '../ui/data-table-view-options';
import { Suspense, useState } from 'react';
import { cn } from '#/lib/utils';
import { IconType, Icons } from '../icons';
import { Switch } from '../ui/switch';

export default function BaseDataTable<TData, TValue>({
  columns,
  data,
  remote,
  totalCount,
  onDelete,
  onDeleteValidate,
  toolbarActions,
  initialColumnVisibility,
  enableRowSelection,
  fetch,
  getRowClassName,
  deleteAction,
  meta,
  hasBorder,
  filterableColumns = [],
  searchableColumns = [],
  rangableColumns = [],
  toolbar = true,
  header = true,
  pager = true,
  isNotRemovePram,
  isViewOption = true,
  manualPagination = true
}: DataTableProps<TData, TValue>) {
  return (
    <div className="flex-col">
      <div className="flex-1 space-y-4 pt-4">
        {remote ? (
          <Suspense
            fallback={
              <div className="mt-14">
                <TableSkeleton
                  column={
                    columns.length -
                    Object.keys(initialColumnVisibility || {}).length -
                    1
                  }
                />
              </div>
            }
          >
            <RemoteDataTable
              fetch={fetch}
              columns={columns}
              data={data}
              totalCount={totalCount!}
              filterableColumns={filterableColumns}
              searchableColumns={searchableColumns}
              rangableColumns={rangableColumns}
              onDelete={onDelete}
              onDeleteValidate={onDeleteValidate}
              initialColumnVisibility={initialColumnVisibility}
              toolbarActions={toolbarActions}
              enableRowSelection={enableRowSelection}
              getRowClassName={getRowClassName}
              deleteAction={deleteAction}
              meta={meta}
              isNotRemovePram={isNotRemovePram}
              isViewOption={isViewOption}
              manualPagination={manualPagination}
              pager={pager}
            />
          </Suspense>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            toolbar={toolbar}
            header={header}
            pager={pager}
            filterableColumns={filterableColumns}
            searchableColumns={searchableColumns}
            rangableColumns={rangableColumns}
            onDelete={onDelete}
            onDeleteValidate={onDeleteValidate}
            initialColumnVisibility={initialColumnVisibility}
            toolbarActions={toolbarActions}
            enableRowSelection={enableRowSelection}
            getRowClassName={getRowClassName}
            deleteAction={deleteAction}
            meta={meta}
            hasBorder={hasBorder}
            isNotRemovePram={isNotRemovePram}
            isViewOption={isViewOption}
          />
        )}
      </div>
    </div>
  );
}

export const CheckboxColumn = (checkAll?: boolean) => ({
  id: 'select',
  header:
    checkAll !== false
      ? ({ table }: any) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value: boolean) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
            className="translate-y-[2px]"
          />
        )
      : undefined,
  cell: ({ row }: any) => {
    return (
      <Checkbox
        disabled={!row.getCanSelect()}
        checked={row.getIsSelected()}
        onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    );
  },
  enableSorting: false,
  enableHiding: false
});

interface ColumnAction {
  tooltip: string;
  href?: string;
  onClick?: () => void;
  Icon?: IconType;
  disable?: boolean;
  using?: boolean;
}

interface ColumnActionsProps {
  items: ColumnAction[];
}

interface ColumnActionSwitchProps {
  item: ColumnAction;
  disabled?: boolean;
}

export const ColumnActions = ({ items }: ColumnActionsProps) => {
  return (
    <div className="flex space-x-2">
      {items.map(({ tooltip, href, onClick, Icon, disable, using }, idx) => {
        if (href) {
          return (
            <Tooltip key={idx} text={tooltip}>
              {disable && Icon ? (
                <Icon className="h-4 w-4 cursor-not-allowed opacity-50" />
              ) : (
                <Link href={href} prefetch={false} className="table-action-btn">
                  {Icon && <Icon className="h-4 w-4" />}
                </Link>
              )}
            </Tooltip>
          );
        } else if (Icon) {
          return (
            <Tooltip key={idx} text={tooltip}>
              <Button
                type="button"
                disabled={disable}
                variant="ghost"
                size="table"
                onClick={onClick}
              >
                <Icon
                  className={cn(
                    'h-4 w-4',
                    Icon === Icons.spinner ? 'btn-spinner' : ''
                  )}
                />
              </Button>
            </Tooltip>
          );
        } else {
          return (
            <Button
              key={idx}
              type="button"
              size={'sm'}
              disabled={disable}
              variant="outline"
              onClick={onClick}
              className={
                using
                  ? 'border-primary text-primary bg-primary/10 hover:bg-primary/15 hover:text-primary'
                  : 'border-red-500 text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-500 '
              }
            >
              {tooltip}
            </Button>
          );
        }
      })}
    </div>
  );
};

export const ColumnActionSwitch = ({
  item,
  disabled
}: ColumnActionSwitchProps) => {
  const [using, setUsing] = useState(item.using);

  const handleClick = async () => {
    if (item.onClick) {
      await item.onClick();
    }
    setUsing((prev) => !prev);
  };

  return (
    <div className="flex space-x-2">
      <div>
        <Switch checked={using} onClick={handleClick} disabled={disabled} />
      </div>
    </div>
  );
};

interface TableSkeletonProps {
  column?: number;
  row?: number;
  header?: boolean;
}

export function TableSkeleton({
  column = 2,
  row = 10,
  header = true
}: TableSkeletonProps) {
  return (
    <Skeleton
      role="status"
      className="w-full bg-transparent pt-4 space-y-4 border divide-y divide-[#104573]/10 rounded dark:divide-[#104573]/20 md:p-6 dark:border-[#104573]/20"
    >
      {header && (
        <div className="h-2.5 bg-[#104573]/20 rounded-full dark:bg-[#104573]/30 w-48"></div>
      )}
      {Array.from(Array(row)).map((_, idx) => (
        <div key={idx} className="flex items-center justify-between pt-4">
          {Array.from(Array(column)).map((_, cIdx) => (
            <div
              key={cIdx}
              className="h-2.5 bg-[#104573]/20 rounded-full dark:bg-[#104573]/30 w-24 mb-2"
            ></div>
          ))}
        </div>
      ))}

      <span className="sr-only">Loading...</span>
    </Skeleton>
  );
}

export const getColumnVisibleState = (
  searchParams: ReadonlyURLSearchParams
) => ({
  ...(searchParams
    .get(COLUMN_VISIBLE_SHOW_KEY)
    ?.split('.')
    .reduce((p: any, l) => {
      p[l] = true;
      return p;
    }, {}) ?? {}),
  ...(searchParams
    .get(COLUMN_VISIBLE_HIDE_KEY)
    ?.split('.')
    .reduce((p: any, l) => {
      p[l] = false;
      return p;
    }, {}) ?? {})
});
